/**
 * Error code service for tenant errors
 * Maps non-readable tokens to user-friendly messages
 * This prevents exposing internal error details in URLs
 */
export class ErrorCodesService {
  // Non-readable tokens that don't hint at internal system
  private static readonly ERROR_TOKENS: Record<string, string> = {
    'E001': 'The requested school account could not be found.',
    'E002': 'The requested school account is not currently active.',
    'E003': 'Unable to locate the school automatically. Enter or select your school account to continue.',
    'E004': 'Unable to validate the school account. Please try again or contact support.',
    'E005': 'Unable to connect to the school account service. Please try again later.',
    'E999': 'An unexpected error occurred. Please contact support for assistance.',
  };

  // Internal mapping from error types to tokens (not exposed)
  private static readonly ERROR_TYPE_TO_TOKEN: Record<string, string> = {
    'TENANT_NOT_FOUND': 'E001',
    'TENANT_INACTIVE': 'E002',
    'TENANT_EXTRACTION_FAILED': 'E003',
    'TENANT_VALIDATION_FAILED': 'E004',
    'TENANT_API_ERROR': 'E005',
    'UNKNOWN_ERROR': 'E999',
  };

  /**
   * Generate a non-readable error token from an error message
   */
  static getErrorToken(errorMessage: string): string {
    // Map common error messages to internal error types
    let errorType = 'UNKNOWN_ERROR';
    
    if (errorMessage.includes('Tenant ID could not be extracted')) {
      errorType = 'TENANT_EXTRACTION_FAILED';
    } else if (errorMessage.includes('not found')) {
      errorType = 'TENANT_NOT_FOUND';
    } else if (errorMessage.includes('not active') || errorMessage.includes('inactive')) {
      errorType = 'TENANT_INACTIVE';
    } else if (errorMessage.includes('validate') || errorMessage.includes('validation')) {
      errorType = 'TENANT_VALIDATION_FAILED';
    } else if (errorMessage.includes('API') || errorMessage.includes('fetch') || errorMessage.includes('network')) {
      errorType = 'TENANT_API_ERROR';
    }
    
    // Return the non-readable token
    return this.ERROR_TYPE_TO_TOKEN[errorType] || this.ERROR_TYPE_TO_TOKEN['UNKNOWN_ERROR'];
  }

  /**
   * Map internal error type directly to a URL-safe token.
   */
  static getErrorTokenByType(errorType: string): string {
    return this.ERROR_TYPE_TO_TOKEN[errorType] || this.ERROR_TYPE_TO_TOKEN['UNKNOWN_ERROR'];
  }

  /**
   * Get user-friendly message from error token
   */
  static getErrorMessage(token: string): string {
    return this.ERROR_TOKENS[token] || this.ERROR_TOKENS['E999'];
  }

  /**
   * Validate and decode error token from URL parameter
   */
  static decodeErrorToken(token: string): string {
    // Validate token format (should be E followed by 3 digits)
    if (/^E\d{3}$/.test(token)) {
      return token;
    }
    
    // Invalid token, return default
    return 'E999';
  }
}

